import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { DateTemplate } from '../../../../../../../../components/DateTemplate'
import { StatusTag } from '../../../../../../../../components/tableTemplates'
import { calculateBreakpointWidth } from '../../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../../ui-config'
import { useDeliveryForecastItemById } from '../../../../../../data'

type Props = {
  deliveryForecastId: string
  itemId: string
  rowData: any
}

export const InfoSplitItem: React.FC<Props> = ({
  deliveryForecastId,
  itemId,
  rowData,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()
  const [visible, setVisible] = useState(false)
  const { data, isLoading } = useDeliveryForecastItemById(
    deliveryForecastId,
    itemId,
  )

  // ************************************************************
  // components
  // ************************************************************
  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={data?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationProductId
  const confirmationProductIdLabel = (
    <label htmlFor="confirmationProductId" className="text-sm"></label>
  )

  const confirmationProductIdComponent = (
    <InputText
      id="confirmationProductId"
      className="w-full"
      value={data?.ConfirmationProductId}
      disabled
    />
  )

  const confirmationProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={confirmationProductIdComponent}
      label={confirmationProductIdLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={data?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // buyerDeliveryDate
  const buyerDeliveryDateLabel = (
    <label htmlFor="buyerDeliveryDate" className="text-sm"></label>
  )

  const buyerDeliveryDateComponent = (
    <Calendar
      id="buyerDeliveryDate"
      value={
        rowData.BuyerDeliveryDate
          ? new Date(rowData.BuyerDeliveryDate)
          : undefined
      }
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const buyerDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={buyerDeliveryDateComponent}
      label={buyerDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label htmlFor="confirmationDeliveryDate" className="text-sm"></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={new Date(rowData.ConfirmationDeliveryDate)}
      className="w-full"
      showButtonBar
      showIcon
      dateTemplate={(date: any) => (
        <DateTemplate
          date={date}
          // desiredDeliveryDate={data?.ConfirmationDeliveryDate}
        />
      )}
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  //  buyerDeliveryQuantity
  const buyerDeliveryQuantityLabel = (
    <label htmlFor="buyerDeliveryQuantity" className="text-sm"></label>
  )

  const buyerDeliveryQuantityComponent = (
    <InputNumber
      id="buyerDeliveryQuantity"
      className="w-full"
      value={rowData.BuyerDeliveryQuantity}
      locale={language}
      disabled
    />
  )

  const buyerDeliveryQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={buyerDeliveryQuantityComponent}
      label={buyerDeliveryQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryQuantity
  const confirmationDeliveryQuantityLabel = (
    <label htmlFor="confirmationDeliveryQuantity" className="text-sm"></label>
  )

  const confirmationDeliveryQuantityComponent = (
    <InputNumber
      id="confirmationDeliveryQuantity"
      value={rowData.ConfirmationDeliveryQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.infoSplitItem}
      component={confirmationDeliveryQuantityComponent}
      label={confirmationDeliveryQuantityLabel}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.info.dialog.title',
          )}
        </span>

        <StatusTag status={rowData.Status} />
      </div>
    </>
  )

  // handleClose
  const handleOpen = () => {
    setVisible(true)
  }

  // handleClose
  const handleClose = () => {
    setVisible(false)
  }

  // footer
  const footer = (
    <Button
      label={t('common.buttons.back')}
      icon="pi pi-check"
      onClick={handleClose}
      autoFocus
      className="mt-3 w-full md:w-auto"
    />
  )

  return (
    <>
      <Button
        icon="pi pi-info-circle"
        className="p-button-rounded p-button-text mr-2"
        onClick={handleOpen}
        visible={config.deliveryForecast.infoSplitItem.enabled !== false}
      />

      <Dialog
        header={header}
        visible={visible}
        style={{ width: '55rem' }}
        breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {/* buyerProductId */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
          <div className="col-12 md:col-6">{confirmationProductIdWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerProductDescription */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">
            {buyerProductDescriptionWrapper}
          </div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerDeliveryDate */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerDeliveryDateWrapper}</div>
          <div className="col-12 md:col-6">
            {confirmationDeliveryDateWrapper}
          </div>
        </div>

        <Divider className="md:hidden" />

        {/* buyerDeliveryQuantity */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{buyerDeliveryQuantityWrapper}</div>
          <div className="col-12 md:col-6">
            {confirmationDeliveryQuantityWrapper}
          </div>
        </div>
      </Dialog>
    </>
  )
}
